import { Route, Routes } from '@angular/router';

export interface SupportPageAccessOption {
  section: string;
  label: string;
  description: string;
  route: string;
  permissionCodes: string[];
}

export const SUPPORT_SETTINGS_ROUTE = '/dashboard/super-admin/configuration/support';
export const SUPPORT_DEFAULT_ALLOWED_ROUTES = new Set<string>();

const SECTION_LABELS: Record<string, string> = {
  admin: 'Administration',
  configuration: 'Configuration',
  subscriptions: 'Subscriptions',
  payments: 'Financial',
  mailbox: 'Settings',
  profile: 'Settings',
  settings: 'Settings',
  properties: 'Management',
  tenants: 'Management',
  landlords: 'Management',
  leases: 'Management',
  maintenance: 'Operations',
  messages: 'Operations',
  reviews: 'Operations',
  testimonials: 'Operations',
  careers: 'Operations',
  referral: 'Operations',
  referrals: 'Operations'
};

function normalizePathSegments(path?: string): string[] {
  return (path ?? '')
    .split('/')
    .map(segment => segment.trim())
    .filter(Boolean);
}

function joinRouteSegments(parentSegments: string[], route: Route): string[] {
  return [...parentSegments, ...normalizePathSegments(route.path)];
}

function buildRoutePath(segments: string[]): string {
  return `/${segments.join('/')}`.replace(/\/+/g, '/');
}

function getSupportPermissionCodes(route: Route): string[] {
  const permissionCodes = route.data?.['supportPermissions'];
  if (!Array.isArray(permissionCodes)) {
    return [];
  }

  return Array.from(
    new Set(
      permissionCodes.filter(
        (code): code is string => typeof code === 'string' && code.trim().length > 0
      )
    )
  );
}

function isSupportManagedRoute(route: Route, fullRoutePath: string): boolean {
  if (route.redirectTo || fullRoutePath === SUPPORT_SETTINGS_ROUTE) {
    return false;
  }

  if (!fullRoutePath.startsWith('/dashboard/super-admin')) {
    return false;
  }

  if (fullRoutePath.includes('/:')) {
    return false;
  }

  return getSupportPermissionCodes(route).length > 0;
}

function resolveSupportSection(routeSegments: string[]): string {
  return SECTION_LABELS[routeSegments[2]] ?? 'Other';
}

function normalizeRouteTitle(title: string, routeSegments: string[]): string {
  if (title === 'Super Admin Dashboard') {
    return 'Dashboard';
  }

  if (title.endsWith(' Configuration')) {
    return title.slice(0, -' Configuration'.length);
  }

  if (title.endsWith(' Management')) {
    return title.slice(0, -' Management'.length);
  }

  if (routeSegments[2] === 'mailbox') {
    return 'Notifications';
  }

  return title;
}

function resolveSupportLabel(route: Route, routeSegments: string[]): string {
  if (typeof route.title === 'string' && route.title.trim().length > 0) {
    return normalizeRouteTitle(route.title, routeSegments);
  }

  const lastSegment = routeSegments[routeSegments.length - 1] ?? 'page';
  return lastSegment
    .split('-')
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function buildSupportPageOption(route: Route, routeSegments: string[]): SupportPageAccessOption {
  const label = resolveSupportLabel(route, routeSegments);

  return {
    section: resolveSupportSection(routeSegments),
    label,
    description: `Open the ${label} page.`,
    route: buildRoutePath(routeSegments),
    permissionCodes: getSupportPermissionCodes(route)
  };
}

function collectSupportPageOptions(
  routes: Routes,
  parentSegments: string[] = [],
  options: SupportPageAccessOption[] = []
): SupportPageAccessOption[] {
  for (const route of routes) {
    const routeSegments = joinRouteSegments(parentSegments, route);
    const fullRoutePath = buildRoutePath(routeSegments);

    if (isSupportManagedRoute(route, fullRoutePath)) {
      options.push(buildSupportPageOption(route, routeSegments));
    }

    if (route.children?.length) {
      collectSupportPageOptions(route.children, routeSegments, options);
    }
  }

  return options;
}

export function getSupportPageAccessOptions(
  routes: Routes,
  availablePermissionCodes: string[] = []
): SupportPageAccessOption[] {
  const uniqueOptions = new Map<string, SupportPageAccessOption>();

  for (const option of collectSupportPageOptions(routes)) {
    uniqueOptions.set(option.route, option);
  }

  const options = Array.from(uniqueOptions.values());
  if (availablePermissionCodes.length === 0) {
    return options;
  }

  const availableCodeSet = new Set(availablePermissionCodes);
  return options.filter(option => option.permissionCodes.some(code => availableCodeSet.has(code)));
}

export function collectSupportPermissionCodes(permissionCodes: string[]): string[] {
  return Array.from(
    new Set(permissionCodes.filter(code => typeof code === 'string' && code.trim().length > 0))
  );
}

export function findSupportPageAccessOptionByRoute(
  routePath: string,
  routes: Routes
): SupportPageAccessOption | undefined {
  const normalizedRoute = routePath.split('?')[0];

  return getSupportPageAccessOptions(routes)
    .filter(option => normalizedRoute === option.route || normalizedRoute.startsWith(`${option.route}/`))
    .sort((left, right) => right.route.length - left.route.length)[0];
}

export function resolveSupportAllowedRoutes(permissionCodes: string[], routes: Routes): Set<string> {
  const assignedPermissionCodes = new Set(permissionCodes);

  return new Set(
    getSupportPageAccessOptions(routes)
      .filter(option => option.permissionCodes.some(code => assignedPermissionCodes.has(code)))
      .map(option => option.route)
  );
}

export function findFirstAccessibleSupportPage(
  permissionCodes: string[],
  routes: Routes
): SupportPageAccessOption | undefined {
  const assignedPermissionCodes = new Set(permissionCodes);

  return getSupportPageAccessOptions(routes).find(option =>
    option.permissionCodes.some(code => assignedPermissionCodes.has(code))
  );
}
